/*
 * TP Trips Panel — ASTAT admin
 * - Create trip
 * - Assign to TP driver → generates URL + fires SMS
 * - Live status updates (SSE)
 * - Copy driver URL, resend SMS
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import TPGoogleMap from "../../components/TPGoogleMap";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import PhoneInput, { isValidE164 } from "../../components/PhoneInput";
import { confirm } from "../../components/ConfirmModal";
import ErrorBoundary from "../../components/ErrorBoundary";
import StatusLegend from "../../components/StatusLegend";
import OnboardingTour from "../../components/OnboardingTour";
import SMSComplianceNotice from "../../components/SMSComplianceNotice";
import PendingReviewBanner from "../../components/PendingReviewBanner";
import BroadcastInFlightBanner from "../../components/BroadcastInFlightBanner";
import { PulseOnChange } from "../../components/live/LiveWorld";

const TP = "/tpt";

// -------- helpers use apiClient (auto-adds bearer) --------

const STATUS_COLORS = {
  ACCEPTED: "bg-slate-100 text-slate-700",
  OFFERED_TP: "bg-blue-100 text-blue-800",
  ASSIGNED: "bg-amber-100 text-amber-800",
  EN_ROUTE: "bg-forest text-white",
  ARRIVED_PU: "bg-violet-600 text-white",
  ON_TRIP: "bg-blue-600 text-white",
  ARRIVED_DO: "bg-violet-700 text-white",
  COMPLETED: "bg-green-600 text-white",
  NO_SHOW_CONFIRMED: "bg-red-600 text-white",
  REASSIGN_NEEDED: "bg-red-100 text-red-800",
  CANCELLED: "bg-slate-300 text-slate-800",
};

function StatusPill({ status }) {
  return (
    <PulseOnChange value={status} color="#8B5CF6">
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_COLORS[status] || "bg-slate-100"}`}>{status}</span>
    </PulseOnChange>
  );
}

// Waiting-for-accept badge: shown when trip is ASSIGNED for > 5 minutes with no acceptance progression
function AwaitingAcceptBadge({ trip }) {
  if (trip.status !== "ASSIGNED") return null;
  const assignedAt = trip.timeline?.slice().reverse().find(e => e.event === "DRIVER_ASSIGNED" || e.event === "SMS_QUEUED" || e.event === "ASSIGNED")?.ts;
  if (!assignedAt) return null;
  const minutesSince = (Date.now() - new Date(assignedAt).getTime()) / 60000;
  if (minutesSince < 5) return null;
  return (
    <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800" title={`Assigned ${Math.round(minutesSince)}m ago`}>
      ⏳ awaiting accept · {Math.round(minutesSince)}m
    </span>
  );
}

function CreateTripDialog({ open, onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    rider_name: "Test Rider",
    rider_phone: "+15550001111",
    mobility_type: "wheelchair",
    medical_notes: "",
    pickup_address: "123 Main St, Detroit, MI",
    pickup_lat: 42.3314, pickup_lng: -83.0458,
    pickup_notes: "Ring buzzer 4B",
    dropoff_address: "Henry Ford Hospital, 2799 W Grand Blvd, Detroit, MI",
    dropoff_lat: 42.3654, dropoff_lng: -83.0783,
    facility_name: "Henry Ford Hospital",
    requested_pickup: new Date(Date.now() + 30 * 60 * 1000).toISOString().slice(0, 16),
    appointment_time: new Date(Date.now() + 90 * 60 * 1000).toISOString().slice(0, 16),
    est_duration: 35, est_distance: 14.2,
    broker_trip_id: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (prefix, place) => {
    // prefix: 'pickup' or 'dropoff'
    setForm((f) => ({
      ...f,
      [`${prefix}_address`]: place.formatted_address,
      [`${prefix}_lat`]: place.lat,
      [`${prefix}_lng`]: place.lng,
    }));
  };
  if (!open) return null;
  const submit = async () => {
    if (!form.rider_name.trim()) { toast.error("Rider name required"); return; }
    if (!isValidE164(form.rider_phone)) { toast.error("Enter a valid rider phone (+1XXXXXXXXXX)"); return; }
    if (!form.pickup_address || form.pickup_lat == null || form.pickup_lng == null) {
      toast.error("Pick a pickup address from the suggestions"); return;
    }
    if (!form.dropoff_address || form.dropoff_lat == null || form.dropoff_lng == null) {
      toast.error("Pick a drop-off address from the suggestions"); return;
    }
    setBusy(true);
    try {
      const payload = {
        priority: "standard",
        broker_trip_id: form.broker_trip_id.trim() || null,
        rider: { name: form.rider_name, phone: form.rider_phone, mobility_type: form.mobility_type, medical_notes: form.medical_notes || null },
        pickup: { address: form.pickup_address, lat: Number(form.pickup_lat), lng: Number(form.pickup_lng), notes: form.pickup_notes },
        dropoff: { address: form.dropoff_address, lat: Number(form.dropoff_lat), lng: Number(form.dropoff_lng) },
        requested_pickup: new Date(form.requested_pickup).toISOString(),
        appointment_time: form.appointment_time ? new Date(form.appointment_time).toISOString() : null,
        vehicle_requirements: { wheelchair: form.mobility_type === "wheelchair", stretcher: form.mobility_type === "stretcher" },
        estimated_duration_min: Number(form.est_duration),
        estimated_distance_mi: Number(form.est_distance),
      };
      const r = await apiClient.post(`${TP}/trips`, payload);
      toast.success("Trip created");
      onCreated(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };
  const renderF = (label, k, type = "text", extra = {}) => (
    <label className="block" key={k}>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`create-trip-${k}`}
        className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" {...extra}/>
    </label>
  );
  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-trip-dialog">
        <div className="text-lg font-bold mb-4">New trip</div>
        <div className="grid grid-cols-2 gap-3">
          {renderF("Rider name", "rider_name")}
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Rider phone</div>
            <PhoneInput
              value={form.rider_phone}
              onChange={(v) => set("rider_phone", v)}
              testid="create-trip-rider_phone"
              required
            />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Mobility</div>
            <select value={form.mobility_type} onChange={(e) => set("mobility_type", e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm">
              <option value="ambulatory">Ambulatory</option>
              <option value="wheelchair">Wheelchair</option>
              <option value="stretcher">Stretcher</option>
              <option value="escort">Escort</option>
            </select>
          </label>
          {renderF("Medical notes", "medical_notes")}
          <label className="block col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Pickup address</div>
            <AddressAutocomplete
              value={form.pickup_address}
              onChange={(v) => set("pickup_address", v)}
              onPlace={(p) => setAddr("pickup", p)}
              testid="create-trip-pickup_address"
              placeholder="Search pickup address…"
            />
            {form.pickup_lat != null && (
              <div className="text-[11px] text-slate-500 mt-1">Verified · {Number(form.pickup_lat).toFixed(5)}, {Number(form.pickup_lng).toFixed(5)}</div>
            )}
          </label>
          {renderF("Pickup notes", "pickup_notes")}
          <label className="block col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Drop-off address</div>
            <AddressAutocomplete
              value={form.dropoff_address}
              onChange={(v) => set("dropoff_address", v)}
              onPlace={(p) => setAddr("dropoff", p)}
              testid="create-trip-dropoff_address"
              placeholder="Search drop-off address…"
            />
            {form.dropoff_lat != null && (
              <div className="text-[11px] text-slate-500 mt-1">Verified · {Number(form.dropoff_lat).toFixed(5)}, {Number(form.dropoff_lng).toFixed(5)}</div>
            )}
          </label>
          {renderF("Facility name", "facility_name")}
          {renderF("Broker/Kaizen trip ID (optional)", "broker_trip_id")}
          {renderF("Pickup at", "requested_pickup", "datetime-local")}
          {renderF("Appointment at", "appointment_time", "datetime-local")}
          {renderF("Est duration (min)", "est_duration", "number")}
          {renderF("Est distance (mi)", "est_distance", "number")}
        </div>
        <SMSComplianceNotice />
        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="create-trip-submit">
            {busy ? "Creating…" : "Create trip"}
          </button>
        </div>
      </div>
    </div>
  );
}

const INDEPENDENT_TP_ID = "__independent__";

/* -------------------------------------------------------------------------
 * PhoneInTripModal — quick booking for a rider who calls in (gap G10, GW-4).
 * Uses the canonical Intake Engine at /api/admin/intake with source=phone_in.
 * No TP assignment, no broker, no advanced options — just get the ride booked.
 * ---------------------------------------------------------------------- */
function PhoneInTripModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    rider_name: "", rider_phone: "", rider_email: "",
    pickup_address: "", dropoff_address: "",
    vehicle_type: "sedan", scheduled_at: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!form.rider_name.trim()) { toast.error("Rider name required"); return; }
    if (!form.pickup_address.trim() || !form.dropoff_address.trim()) {
      toast.error("Both pickup and drop-off addresses are required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        source: "phone_in",
        rider: {
          name: form.rider_name.trim(),
          phone: form.rider_phone.trim() || null,
          email: form.rider_email.trim() || null,
        },
        pickup: { address: form.pickup_address.trim() },
        dropoff: { address: form.dropoff_address.trim() },
        vehicle_type: form.vehicle_type,
        scheduled_at: form.scheduled_at || null,
        notes: form.notes.trim() || null,
      };
      const { data } = await apiClient.post("/admin/intake", payload);
      const meta = data?.meta || {};
      if (meta.duplicate) {
        toast.success(`Existing trip returned (dedupe): ${(meta.trip_id || "").slice(0, 8)}`);
      } else {
        toast.success(`Trip booked · #${(meta.trip_id || "").slice(0, 8)}`);
      }
      setForm({ rider_name: "", rider_phone: "", rider_email: "", pickup_address: "", dropoff_address: "", vehicle_type: "sedan", scheduled_at: "", notes: "" });
      onCreated?.();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || e?.message || "Booking failed";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}
         onClick={() => !busy && onClose?.()}
         data-testid="phonein-modal-backdrop">
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 16, padding: 24, width: "min(520px, 94vw)", maxHeight: "92vh", overflow: "auto", border: "1px solid #E2E0D8", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
        data-testid="phonein-modal">
        <div className="text-lg font-bold text-slate_navy">Phone-in ride</div>
        <div className="text-xs text-slate-500 mb-4">Rider called in. Book them in 60 seconds — routes through the Intake Engine, no manual TP assignment needed.</div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input value={form.rider_name} onChange={e => setForm({ ...form, rider_name: e.target.value })}
              placeholder="Rider name *" data-testid="phonein-name"
              className="px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" />
            <input value={form.rider_phone} onChange={e => setForm({ ...form, rider_phone: e.target.value })}
              placeholder="Phone (+1…)" data-testid="phonein-phone"
              className="px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" />
          </div>
          <input value={form.rider_email} onChange={e => setForm({ ...form, rider_email: e.target.value })}
            placeholder="Email (optional)" data-testid="phonein-email"
            className="w-full px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" />
          <input value={form.pickup_address} onChange={e => setForm({ ...form, pickup_address: e.target.value })}
            placeholder="Pickup address *" data-testid="phonein-pickup"
            className="w-full px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" />
          <input value={form.dropoff_address} onChange={e => setForm({ ...form, dropoff_address: e.target.value })}
            placeholder="Drop-off address *" data-testid="phonein-dropoff"
            className="w-full px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.vehicle_type} onChange={e => setForm({ ...form, vehicle_type: e.target.value })}
              data-testid="phonein-vehicle-type"
              className="px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]">
              <option value="sedan">Sedan</option>
              <option value="wheelchair">Wheelchair</option>
              <option value="stretcher">Stretcher</option>
              <option value="xl">XL / Van</option>
            </select>
            <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })}
              data-testid="phonein-scheduled"
              className="px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" placeholder="Now if empty" />
          </div>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Notes for driver (optional)" rows={2}
            data-testid="phonein-notes"
            className="w-full px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" />
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} disabled={busy}
            className="flex-1 py-2 rounded-full font-semibold border border-slate-300" style={{ background: "#fff", color: "#334155" }}>Cancel</button>
          <button onClick={submit} disabled={busy}
            data-testid="phonein-submit"
            className="flex-1 py-2 rounded-full font-bold text-white"
            style={{ background: "#2B4C3B", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Booking…" : "Book ride"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignDialog({ trip, providers, drivers, onClose, onAssigned, refreshDrivers }) {
  // Compute the initial TP: prefer trip.assigned_tp, else the FIRST TP that
  // actually has at least one driver on file (matches user expectation:
  // "opening reassign should show a TP whose drivers I can pick from").
  const initialTpId = useMemo(() => {
    if (trip?.assigned_tp?.tp_id) return trip.assigned_tp.tp_id;
    const tpWithDrivers = providers.find(p => drivers.some(d => d.tp_id === p.tp_id));
    if (tpWithDrivers) return tpWithDrivers.tp_id;
    // If any independent drivers exist, default to that virtual bucket
    if (drivers.some(d => !d.tp_id || d.owner_type === "astat")) return INDEPENDENT_TP_ID;
    return providers[0]?.tp_id || "";
  }, [trip, providers, drivers]);
  const [tpId, setTpId] = useState(initialTpId);
  const [driverId, setDriverId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showUncleared, setShowUncleared] = useState(false);
  const [forceUncleared, setForceUncleared] = useState(false);
  // Keep tpId in sync if trip changes
  useEffect(() => { setTpId(initialTpId); }, [initialTpId]);
  // Refresh drivers list when the dialog opens so TP-added drivers show up
  // without waiting for the 6-second poll to tick.
  useEffect(() => { if (trip && refreshDrivers) refreshDrivers(); }, [trip, refreshDrivers]);
  const tpDrivers = useMemo(() => {
    if (tpId === INDEPENDENT_TP_ID) {
      // Independent drivers: owner_type "astat" OR no tp_id
      return drivers.filter(d => (d.owner_type === "astat") || !d.tp_id);
    }
    return drivers.filter(d => d.tp_id === tpId);
  }, [drivers, tpId]);
  const clearedDrivers = useMemo(() => tpDrivers.filter(d => (d.medexova_credentialing_status || "pending") === "cleared"), [tpDrivers]);
  const unclearedDrivers = useMemo(() => tpDrivers.filter(d => (d.medexova_credentialing_status || "pending") !== "cleared"), [tpDrivers]);
  const displayedDrivers = showUncleared ? [...clearedDrivers, ...unclearedDrivers] : clearedDrivers;
  useEffect(() => { setDriverId(displayedDrivers[0]?.id || ""); }, [tpId, displayedDrivers]);
  if (!trip) return null;
  const selectedDriver = displayedDrivers.find(d => d.id === driverId);
  const selectedIsUncleared = selectedDriver && (selectedDriver.medexova_credentialing_status || "pending") !== "cleared";
  const submit = async () => {
    if (!tpId || !driverId) { toast.error("Pick a TP and driver"); return; }
    if (selectedIsUncleared && !forceUncleared) {
      toast.error("Tick 'VA override' to assign an uncleared driver");
      return;
    }
    if (trip.assigned_driver && trip.assigned_driver.driver_id !== driverId) {
      const ok = await confirm({
        title: "Reassign this trip?",
        body: "The current driver's link will be killed and a new SMS will be sent to the new driver. Continue?",
        confirmLabel: "Reassign",
        tone: "danger",
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      // For independent drivers we send the driver's own tp_id (may be null);
      // backend accepts null and treats it as ASTAT-owned.
      const payloadTpId = tpId === INDEPENDENT_TP_ID ? (selectedDriver?.tp_id || null) : tpId;
      const r = await apiClient.post(`${TP}/trips/${trip.trip_id}/assign`, {
        tp_id: payloadTpId, driver_id: driverId, force_uncleared: !!forceUncleared,
      });
      toast.success(`Assigned · SMS ${r.data?.sms?.ok ? "queued" : "failed"}`);
      onAssigned(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="assign-dialog">
        <div className="text-lg font-bold mb-3">Assign driver</div>
        <div className="text-sm text-slate-600 mb-4">Trip · {trip.rider?.name} · pickup {new Date(trip.schedule?.requested_pickup).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
        <label className="block mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Transportation Provider</div>
          <select value={tpId} onChange={(e) => setTpId(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="assign-tp-select">
            {providers.map(tp => {
              const count = drivers.filter(d => d.tp_id === tp.tp_id).length;
              return <option key={tp.tp_id} value={tp.tp_id}>{tp.name}{count ? ` · ${count} drivers` : " · no drivers"}</option>;
            })}
            {drivers.some(d => (d.owner_type === "astat") || !d.tp_id) && (
              <option value={INDEPENDENT_TP_ID}>
                Independent (MedexOVA-managed) · {drivers.filter(d => (d.owner_type === "astat") || !d.tp_id).length} drivers
              </option>
            )}
          </select>
        </label>
        <label className="block mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-between">
            <span>Driver ({clearedDrivers.length} cleared{unclearedDrivers.length > 0 ? ` · ${unclearedDrivers.length} pending` : ""})</span>
            {unclearedDrivers.length > 0 && (
              <label className="flex items-center gap-1 normal-case tracking-normal text-[10px] text-slate-500">
                <input type="checkbox" checked={showUncleared} onChange={(e) => setShowUncleared(e.target.checked)} data-testid="show-uncleared" />
                Show {unclearedDrivers.length} pending
              </label>
            )}
          </div>
          <select value={driverId} onChange={(e) => setDriverId(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="assign-driver-select">
            {displayedDrivers.length === 0 && <option value="">No cleared drivers for this TP — add & clear via Providers → Drivers</option>}
            {displayedDrivers.map(d => {
              const cred = d.medexova_credentialing_status || "pending";
              const label = cred === "cleared" ? "" : ` · ${cred.toUpperCase()}`;
              return <option key={d.id} value={d.id}>{d.name} · {d.phone}{label}</option>;
            })}
          </select>
          {selectedIsUncleared && (
            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900">
              <div className="font-semibold mb-1">⚠︎ Driver credentialing is <b>{(selectedDriver.medexova_credentialing_status || "pending").toUpperCase()}</b></div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={forceUncleared} onChange={(e) => setForceUncleared(e.target.checked)} data-testid="force-uncleared" />
                <span>VA override — assign anyway. This is audit-logged.</span>
              </label>
            </div>
          )}
        </label>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="assign-submit">
            {busy ? "Assigning…" : "Assign + SMS driver"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * OfferToTPsDialog · Track 1 · Phase D — VA UI
 * ────────────────────────────────────────────
 * Fires POST /api/tpt/trips/{id}/offer to route a trip to N eligible TPs
 * simultaneously.  Whichever TP hits their Accept button first wins; the
 * others see their pool entry flip to "expired" via SSE.
 */
function OfferToTPsDialog({ trip, providers, drivers, onClose, onOffered }) {
  const [selected, setSelected] = useState(new Set());
  const [ttl, setTtl] = useState(10);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset selection whenever the dialog opens on a new trip.
  useEffect(() => { setSelected(new Set()); setNote(""); setTtl(10); }, [trip?.trip_id]);

  if (!trip) return null;

  const eligible = providers.filter(p => {
    if (p.tp_id === trip?.assigned_tp?.tp_id) return false;  // Skip current owner
    const count = drivers.filter(d => d.tp_id === p.tp_id).length;
    return count > 0;  // Only TPs with at least one driver
  });

  const toggle = (tp_id) => {
    const next = new Set(selected);
    if (next.has(tp_id)) next.delete(tp_id); else next.add(tp_id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === eligible.length) setSelected(new Set());
    else setSelected(new Set(eligible.map(p => p.tp_id)));
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("Pick at least one TP"); return; }
    setBusy(true);
    try {
      const r = await apiClient.post(`${TP}/trips/${trip.trip_id}/offer`, {
        tp_ids: Array.from(selected),
        ttl_minutes: Number(ttl) || 10,
        note: note || null,
      });
      toast.success(`Offered to ${r.data.offered_to.length} TPs · expires in ${ttl}m`);
      onOffered && onOffered(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Offer failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="offer-dialog">
        <div className="text-lg font-bold mb-1">Offer to multiple TPs</div>
        <div className="text-xs text-slate-500 mb-4">First accept wins. All other offers auto-expire.</div>

        <div className="text-sm text-slate-600 mb-3">Trip · {trip.rider?.name} · pickup {new Date(trip.schedule?.requested_pickup).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>

        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-slate-500">Eligible Transportation Providers</div>
          <button onClick={toggleAll} className="text-xs text-brandblue underline" data-testid="offer-toggle-all">
            {selected.size === eligible.length && eligible.length > 0 ? "Clear" : "Select all"}
          </button>
        </div>

        <div className="max-h-64 overflow-y-auto border border-[#E2E0D8] rounded-md divide-y divide-[#E9EAE4]" data-testid="offer-tp-list">
          {eligible.length === 0 && (
            <div className="p-4 text-sm text-slate-500 text-center">No other TPs with drivers on file. Assign directly instead.</div>
          )}
          {eligible.map(p => {
            const count = drivers.filter(d => d.tp_id === p.tp_id).length;
            const on = selected.has(p.tp_id);
            return (
              <label key={p.tp_id} className={`flex items-center gap-3 p-3 cursor-pointer ${on ? "bg-forest/5" : "hover:bg-slate-50"}`} data-testid={`offer-tp-row-${p.tp_id}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(p.tp_id)} className="h-4 w-4" data-testid={`offer-tp-check-${p.tp_id}`}/>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate_navy">{p.name}</div>
                  <div className="text-xs text-slate-500">{count} driver{count === 1 ? "" : "s"}</div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">TTL (minutes)</div>
            <input type="number" min="1" max="120" value={ttl} onChange={(e) => setTtl(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="offer-ttl"/>
          </label>
          <label className="block col-span-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Note (optional)</div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Priority · lift required · etc." className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid="offer-note"/>
          </label>
        </div>

        <div className="mt-6 flex justify-between items-center">
          <div className="text-xs text-slate-500">{selected.size} TP{selected.size === 1 ? "" : "s"} selected</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm text-slate-500 px-3 py-2" data-testid="offer-cancel">Cancel</button>
            <button onClick={submit} disabled={busy || selected.size === 0}
              className={`text-sm font-semibold px-5 py-2 rounded-md transition ${selected.size === 0 ? "bg-slate-200 text-slate-400" : "bg-brandblue-ink text-white hover:bg-brandblue-deep"}`}
              data-testid="offer-submit">
              {busy ? "Sending offers…" : `Offer to ${selected.size || 0}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// TripThreadDialog — unified per-trip SMS thread + "share tracking link"
// forward. Same backend (`/api/floor/trips/{id}/thread*`, `/share-link`) The
// Floor's Focus Workspace uses — Live Ops and The Floor read/write one thread.
function TripThreadDialog({ trip, onClose }) {
  const [messages, setMessages] = useState([]);
  const [party, setParty] = useState("driver");
  const [channel, setChannel] = useState("sms");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [ttl, setTtl] = useState(24);
  const [fwdPhone, setFwdPhone] = useState("");
  const [fwdEmail, setFwdEmail] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  useEffect(() => {
    if (!trip) return;
    setMessages([]); setText(""); setLinkResult(null);
    setParty(trip.assigned_driver?.driver_id ? "driver" : "rider");
  }, [trip?.trip_id]);

  useEffect(() => {
    if (!trip) return;
    let cancel = false;
    const pull = async () => {
      try {
        const { data } = await apiClient.get(`/floor/trips/${trip.trip_id}/thread`);
        if (!cancel) setMessages(Array.isArray(data?.messages) ? data.messages : []);
      } catch { /* silent */ }
    };
    pull();
    const t = setInterval(pull, 6000);
    return () => { cancel = true; clearInterval(t); };
  }, [trip?.trip_id]);

  if (!trip) return null;
  const hasRiderPhone = !!trip.rider?.phone;
  const hasDriver = !!trip.assigned_driver?.driver_id;

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await apiClient.post(`/floor/trips/${trip.trip_id}/thread/send`, { party, channel, body: text.trim() });
      setText("");
      const { data } = await apiClient.get(`/floor/trips/${trip.trip_id}/thread`);
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
      toast.success("Sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${channel === "email" ? "Email" : "SMS"} failed`);
    } finally { setSending(false); }
  };

  const genLink = async () => {
    setLinkBusy(true);
    setLinkResult(null);
    try {
      const { data } = await apiClient.post(`/floor/trips/${trip.trip_id}/share-link`, {
        ttl_hours: ttl, forward_phone: fwdPhone.trim() || null, forward_email: fwdEmail.trim() || null,
      });
      setLinkResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "couldn't generate link");
    } finally { setLinkBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="thread-dialog">
        <div className="text-lg font-bold mb-1">{trip.rider?.name || "Trip"} — messages</div>
        <div className="text-xs text-slate-500 mb-4">Unified SMS thread — replies land here automatically.</div>

        <div className="border border-[#E2E0D8] rounded-md max-h-56 overflow-y-auto p-2 space-y-1" data-testid="thread-dialog-messages">
          {messages.length === 0 && <div className="text-sm text-slate-400 text-center py-4">No messages yet</div>}
          {messages.map(m => (
            <div key={m.id} className={`text-sm p-2 rounded-md max-w-[85%] ${m.direction === "out" ? "bg-forest/10 ml-auto text-right" : "bg-slate-100"}`}>
              <div className="text-[10px] uppercase text-slate-400">{m.direction === "out" ? "→" : "←"} {m.party} · {m.channel === "email" ? "✉ email" : "sms"}</div>
              <div>{m.body}</div>
              <div className="text-[10px] text-slate-400">{new Date(m.at).toLocaleTimeString()}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-3">
          <button onClick={() => setParty("rider")} disabled={!hasRiderPhone}
            className={`text-xs px-3 py-1 rounded-full border ${party === "rider" ? "bg-forest text-white border-forest" : "border-[#E2E0D8]"}`}
            data-testid="thread-dialog-party-rider">To rider</button>
          <button onClick={() => setParty("driver")} disabled={!hasDriver}
            className={`text-xs px-3 py-1 rounded-full border ${party === "driver" ? "bg-forest text-white border-forest" : "border-[#E2E0D8]"}`}
            data-testid="thread-dialog-party-driver">To driver</button>
          <span className="w-px bg-[#E2E0D8] mx-1" />
          <button onClick={() => setChannel("sms")}
            className={`text-xs px-3 py-1 rounded-full border ${channel === "sms" ? "bg-brandblue-ink text-white border-brandblue-ink" : "border-[#E2E0D8]"}`}
            data-testid="thread-dialog-channel-sms">SMS</button>
          <button onClick={() => setChannel("email")}
            className={`text-xs px-3 py-1 rounded-full border ${channel === "email" ? "bg-brandblue-ink text-white border-brandblue-ink" : "border-[#E2E0D8]"}`}
            data-testid="thread-dialog-channel-email">Email</button>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
          className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm mt-2" rows={2}
          data-testid="thread-dialog-input" />
        <button onClick={send} disabled={sending || !text.trim()}
          className="mt-2 text-sm font-semibold px-4 py-2 rounded-md bg-brandblue-ink text-white disabled:bg-slate-200 disabled:text-slate-400"
          data-testid="thread-dialog-send">{sending ? "Sending…" : `Send ${channel === "email" ? "email" : "SMS"}`}</button>

        <div className="mt-6 pt-4 border-t border-[#E9EAE4]">
          <div className="text-sm font-semibold mb-1">Share live tracking link</div>
          <div className="text-xs text-slate-500 mb-2">Read-only, auto-expires — forward to a broker if needed.</div>
          <div className="flex gap-2 mb-2">
            {[6, 24, 72].map(h => (
              <button key={h} onClick={() => setTtl(h)}
                className={`text-xs px-3 py-1 rounded-full border ${ttl === h ? "bg-forest text-white border-forest" : "border-[#E2E0D8]"}`}
                data-testid={`thread-dialog-ttl-${h}`}>{h}h</button>
            ))}
          </div>
          <input value={fwdPhone} onChange={(e) => setFwdPhone(e.target.value)} placeholder="Broker phone (optional)"
            className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm mb-2" data-testid="thread-dialog-fwd-phone" />
          <input value={fwdEmail} onChange={(e) => setFwdEmail(e.target.value)} placeholder="Broker email (optional)"
            className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm mb-2" data-testid="thread-dialog-fwd-email" />
          <button onClick={genLink} disabled={linkBusy}
            className="text-sm font-semibold px-4 py-2 rounded-md bg-forest text-white disabled:bg-slate-200"
            data-testid="thread-dialog-gen-link">{linkBusy ? "Generating…" : "Generate & forward"}</button>
          {linkResult?.share_url && (
            <div className="mt-2 text-xs text-slate-600 break-all" data-testid="thread-dialog-link-result">
              {linkResult.share_url}
              <div className="text-slate-400">expires {new Date(linkResult.expires_at).toLocaleString()}</div>
            </div>
          )}
        </div>

        <div className="mt-4 text-right">
          <button onClick={onClose} className="text-sm text-slate-500 px-3 py-2" data-testid="thread-dialog-close">Close</button>
        </div>
      </div>
    </div>
  );
}




function TripRow({ trip, onAssign, onCopyUrl, onResend, onOffer }) {
  const [displayId, setDisplayId] = useState(trip.display_trip_id || null);
  const [copied, setCopied] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  const copyId = async () => {
    if (!displayId) return;
    try { await navigator.clipboard.writeText(displayId); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* no-op */ }
  };
  const generateId = async () => {
    setGenBusy(true);
    try {
      const { data } = await apiClient.post(`${TP}/trips/${trip.trip_id}/generate-display-id`);
      trip.display_trip_id = data.display_trip_id;
      setDisplayId(data.display_trip_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate Trip ID"); }
    finally { setGenBusy(false); }
  };

  return (
    <tr className="border-b border-[#E9EAE4] hover:bg-slate-50" data-testid={`trip-row-${trip.trip_id}`}>
      <td className="py-2 px-3">
        <StatusPill status={trip.status}/>
        <div className="mt-1">
          {displayId ? (
            <span className="text-[10px] font-mono text-slate-500" data-testid={`trip-display-id-${trip.trip_id}`}>
              {displayId}{" "}
              <button onClick={copyId} className="text-[10px] text-brandblue underline" data-testid={`trip-display-id-copy-${trip.trip_id}`}>
                {copied ? "copied" : "copy"}
              </button>
            </span>
          ) : (
            <button onClick={generateId} disabled={genBusy} className="text-[10px] text-brandblue underline" data-testid={`trip-display-id-generate-${trip.trip_id}`}>
              {genBusy ? "…" : "+ Generate Trip ID"}
            </button>
          )}
        </div>
      </td>
      <td className="py-2 px-3">
        <div className="font-medium text-slate_navy">{trip.rider?.name}</div>
        <div className="text-xs text-slate-500">{trip.rider?.mobility_type}</div>
      </td>
      <td className="py-2 px-3 text-sm">
        <div>{(trip.pickup?.address || "").split(",")[0]}</div>
        <div className="text-slate-500 text-xs">→ {(trip.dropoff?.address || "").split(",")[0]}</div>
      </td>
      <td className="py-2 px-3 text-xs text-slate-500">
        {trip.schedule?.requested_pickup ? new Date(trip.schedule.requested_pickup).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
      </td>
      <td className="py-2 px-3 text-sm">
        {trip.assigned_tp?.tp_name || <span className="text-slate-400">unassigned</span>}
      </td>
      <td className="py-2 px-3 text-sm">
        {trip.assigned_driver?.driver_name || "—"}
        {trip.assigned_driver?.driver_phone_masked && <div className="text-xs text-slate-500">{trip.assigned_driver.driver_phone_masked}</div>}
      </td>
      <td className="py-2 px-3 text-right space-x-2">
        {["ACCEPTED", "OFFERED_TP", "OFFERED", "REASSIGN_NEEDED", "ASSIGNED", "TP_ACCEPTED"].includes(trip.status) && (
          <button onClick={() => onAssign(trip)} className="text-xs bg-forest text-white px-3 py-1 rounded-md" data-testid={`assign-btn-${trip.trip_id}`}>
            {["ASSIGNED", "TP_ACCEPTED"].includes(trip.status) ? "Reassign" : "Assign"}
          </button>
        )}
        {["ACCEPTED", "REASSIGN_NEEDED", "EXPIRED_OFFER"].includes(trip.status) && onOffer && (
          <button onClick={() => onOffer(trip)} className="text-xs bg-brandblue-ink text-white px-3 py-1 rounded-md" data-testid={`offer-btn-${trip.trip_id}`}>
            Offer to TPs
          </button>
        )}
        {trip.driver_url?.token_jti && ["ASSIGNED", "EN_ROUTE", "ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"].includes(trip.status) && (
          <>
            <button onClick={() => onCopyUrl(trip)} className="text-xs border border-[#E2E0D8] px-2 py-1 rounded-md" data-testid={`copyurl-btn-${trip.trip_id}`}>Copy link</button>
            <button onClick={() => onResend(trip)} className="text-xs border border-[#E2E0D8] px-2 py-1 rounded-md" data-testid={`resend-btn-${trip.trip_id}`}>Resend SMS</button>
          </>
        )}
      </td>
    </tr>
  );
}

// AddLegDialog — chain a return (or additional) leg onto an existing trip.
// A round-trip is modelled as N one-way trips linked via parent_trip_id.
function AddLegDialog({ trip, onClose, onCreated }) {
  const [pickupAddr, setPickupAddr] = useState("");
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropoffAddr, setDropoffAddr] = useState("");
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [pickupTime, setPickupTime] = useState("");
  const [notes, setNotes] = useState("");
  const [legs, setLegs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!trip) return;
    setPickupAddr(trip.dropoff?.address || "");
    setPickupPlace(trip.dropoff?.lat && trip.dropoff?.lng ? { lat: trip.dropoff.lat, lng: trip.dropoff.lng } : null);
    setDropoffAddr(trip.pickup?.address || "");
    setDropoffPlace(trip.pickup?.lat && trip.pickup?.lng ? { lat: trip.pickup.lat, lng: trip.pickup.lng } : null);
    if (trip.schedule?.requested_pickup) {
      try {
        const d = new Date(trip.schedule.requested_pickup);
        d.setHours(d.getHours() + 2);
        const pad = (n) => String(n).padStart(2, "0");
        setPickupTime(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      } catch { setPickupTime(""); }
    }
    apiClient.get(`${TP}/trips/${trip.trip_id}/legs`)
      .then(({ data }) => setLegs(data.legs || []))
      .catch(() => setLegs([]));
  }, [trip]);

  if (!trip) return null;

  const submit = async () => {
    if (!pickupAddr || !dropoffAddr) { toast.error("Both addresses required"); return; }
    if (!pickupTime) { toast.error("Return pickup time required"); return; }
    setBusy(true);
    try {
      const iso = new Date(pickupTime).toISOString();
      const { data } = await apiClient.post(`${TP}/trips/${trip.trip_id}/add-leg`, {
        pickup_address: pickupAddr,
        pickup_lat: pickupPlace?.lat,
        pickup_lng: pickupPlace?.lng,
        dropoff_address: dropoffAddr,
        dropoff_lat: dropoffPlace?.lat,
        dropoff_lng: dropoffPlace?.lng,
        requested_pickup: iso,
        notes: notes || null,
      });
      toast.success(`Leg ${data.leg_number} added · trip ${data.trip_id.slice(0, 8)}`);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add leg failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="add-leg-dialog">
        <div className="text-lg font-bold text-slate_navy mb-1">Add trip leg</div>
        <div className="text-xs text-slate-500 mb-4">
          Chain a return or intermediate leg to <b>{trip.rider?.name}</b>&apos;s trip. Each leg is its own dispatchable trip; billing groups them together.
        </div>
        {legs.length > 0 && (
          <div className="mb-4 border border-[#E9EAE4] rounded-md p-2 bg-slate-50">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Existing legs ({legs.length})</div>
            {legs.map(l => (
              <div key={l.trip_id} className="flex justify-between text-xs py-0.5" data-testid={`existing-leg-${l.trip_id}`}>
                <span>Leg {l.leg_number || 1}</span>
                <span className="text-slate-500">{l.status}</span>
                <span className="text-slate-500">{l.pickup?.address?.split(",")[0] || "—"} → {l.dropoff?.address?.split(",")[0] || "—"}</span>
              </div>
            ))}
          </div>
        )}
        <label className="block mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Pickup address</div>
          <AddressAutocomplete
            value={pickupAddr}
            onChange={setPickupAddr}
            onPlace={(p) => setPickupPlace({ lat: p.lat, lng: p.lng })}
            testid="leg-pickup-input"
          />
        </label>
        <label className="block mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Drop-off address</div>
          <AddressAutocomplete
            value={dropoffAddr}
            onChange={setDropoffAddr}
            onPlace={(p) => setDropoffPlace({ lat: p.lat, lng: p.lng })}
            testid="leg-dropoff-input"
          />
        </label>
        <label className="block mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Return pickup time</div>
          <input
            type="datetime-local"
            value={pickupTime}
            onChange={(e) => setPickupTime(e.target.value)}
            className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"
            data-testid="leg-time-input"
          />
        </label>
        <label className="block mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Notes (optional)</div>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" placeholder="Any dispatch notes for the driver" data-testid="leg-notes-input"/>
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold disabled:opacity-50" data-testid="leg-submit-btn">
            {busy ? "Adding…" : "Add leg"}
          </button>
        </div>
      </div>
    </div>
  );
}



function BroadcastDialog({ trip, providers, onClose, onDone }) {
  const [scope, setScope] = useState("tp");
  const [tpId, setTpId] = useState(trip?.assigned_tp?.tp_id || providers[0]?.tp_id || "");
  const [tpIds, setTpIds] = useState([]);
  const [includeIndependent, setIncludeIndependent] = useState(true);
  const [onlyAvail, setOnlyAvail] = useState(true);
  const [busy, setBusy] = useState(false);
  if (!trip) return null;
  const submit = async () => {
    if (scope === "network") {
      const ok = await confirm({
        title: "Broadcast to entire network?",
        body: `This will SMS every eligible driver${includeIndependent ? " (including independents)" : ""}. First to accept wins. Do you want to continue?`,
        confirmLabel: "Broadcast to network",
        tone: "danger",
      });
      if (!ok) return;
    } else if (scope === "tps" && tpIds.length >= 3) {
      const ok = await confirm({
        title: `Broadcast to ${tpIds.length} TPs?`,
        body: "SMS will fire to every eligible driver at these providers. Continue?",
        confirmLabel: "Broadcast",
        tone: "danger",
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const payload = { scope, only_available: onlyAvail };
      if (scope === "tp") payload.tp_id = tpId;
      if (scope === "tps") payload.tp_ids = tpIds;
      if (scope === "network") payload.include_independent = includeIndependent;
      const r = await apiClient.post(`${TP}/trips/${trip.trip_id}/broadcast`, payload);
      toast.success(`Broadcast sent to ${r.data.count} driver(s)`);
      onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="broadcast-dialog">
        <div className="text-lg font-bold mb-1">Broadcast trip</div>
        <div className="text-xs text-slate-500 mb-4">First driver to accept wins. Sibling links auto-revoke.</div>
        <div className="space-y-3 mb-4">
          <label className="flex items-center gap-2"><input type="radio" checked={scope==="tp"} onChange={() => setScope("tp")}/> One TP</label>
          {scope === "tp" && (
            <select value={tpId} onChange={(e) => setTpId(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm ml-6">
              {providers.map(p => <option key={p.tp_id} value={p.tp_id}>{p.name}</option>)}
            </select>
          )}
          <label className="flex items-center gap-2"><input type="radio" checked={scope==="tps"} onChange={() => setScope("tps")}/> Multiple TPs</label>
          {scope === "tps" && (
            <div className="ml-6 space-y-1 max-h-40 overflow-y-auto border border-[#E2E0D8] rounded-md p-2">
              {providers.map(p => (
                <label key={p.tp_id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={tpIds.includes(p.tp_id)} onChange={(e) => setTpIds(e.target.checked ? [...tpIds, p.tp_id] : tpIds.filter(i => i !== p.tp_id))}/>
                  {p.name}
                </label>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2"><input type="radio" checked={scope==="network"} onChange={() => setScope("network")}/> Entire network</label>
          {scope === "network" && (
            <label className="flex items-center gap-2 text-xs ml-6">
              <input type="checkbox" checked={includeIndependent} onChange={(e) => setIncludeIndependent(e.target.checked)}/>
              Include independent (non-TP) MedexOVA drivers
            </label>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm mb-4">
          <input type="checkbox" checked={onlyAvail} onChange={(e) => setOnlyAvail(e.target.checked)}/>
          Only text drivers currently marked <b>available</b>
        </label>
        <SMSComplianceNotice compact className="mb-3"/>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-md bg-forest text-white text-sm font-semibold" data-testid="broadcast-submit">
            {busy ? "Sending…" : "Broadcast"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClosestDriverPanel({ trip, onAssign }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!trip) { setData(null); return; }
    try {
      const r = await apiClient.get(`${TP}/map/candidates`, { params: { trip_id: trip.trip_id } });
      setData(r.data);
    } catch (e) { toast.error("Candidates load failed"); }
  }, [trip]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  if (!trip) return (
    <div className="bg-white border border-[#E9EAE4] rounded-xl p-6 text-sm text-slate-500 text-center">
      Click a trip to see closest drivers and choose one.
    </div>
  );
  const doAssign = async (driver) => {
    setBusy(true);
    try {
      await onAssign(trip, driver);
      await load();
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-[#E9EAE4] rounded-xl overflow-hidden">
      <div className="p-3 border-b border-[#E9EAE4] flex justify-between items-center">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Selected trip</div>
          <div className="font-semibold text-slate-900">{data?.trip?.rider_name || trip.rider?.name}</div>
        </div>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{trip.status}</span>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        <div className="text-xs uppercase tracking-wider text-slate-500 px-3 py-2 bg-slate-50">Drivers · closest first</div>
        {(data?.drivers || []).map(d => (
          <div key={d.driver_id} className="p-3 border-b border-[#E9EAE4] last:border-0 hover:bg-slate-50" data-testid={`candidate-${d.driver_id}`}>
            <div className="flex justify-between items-center">
              <div>
                <div className="font-medium text-slate-900">{d.name}</div>
                <div className="text-xs text-slate-500">
                  <span className={`inline-block w-2 h-2 rounded-full mr-1 ${d.shift_status==="available"?"bg-green-500":d.shift_status==="loaded"?"bg-blue-500":d.shift_status==="busy"?"bg-amber-500":"bg-slate-400"}`}/>
                  {d.shift_status} · {d.owner_type === "tp" ? "TP driver" : "independent"}
                </div>
              </div>
              <div className="text-right">
                {d.distance_to_pickup_mi != null ? (
                  <div className="text-sm font-semibold text-slate-900">{d.distance_to_pickup_mi} mi</div>
                ) : (
                  <div className="text-xs text-slate-400">no GPS</div>
                )}
                {d.eta_to_pickup_min != null && <div className="text-xs text-slate-500">~{d.eta_to_pickup_min} min</div>}
              </div>
            </div>
            {["ACCEPTED","OFFERED_TP","ASSIGNED","REASSIGN_NEEDED"].includes(trip.status) && (
              <button onClick={() => doAssign(d)} disabled={busy} className="mt-2 text-xs bg-forest text-white px-3 py-1 rounded-md" data-testid={`pick-${d.driver_id}`}>
                {trip.status === "ASSIGNED" ? "Reassign to" : "Assign to"} {d.name.split(" ")[0]}
              </button>
            )}
          </div>
        ))}
        {(!data || data.drivers?.length === 0) && <div className="p-4 text-sm text-slate-500">No eligible drivers.</div>}
      </div>
    </div>
  );
}

function FloorMigrationBanner({ where }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: "12px", padding: "10px 14px", marginBottom: "12px",
      background: "linear-gradient(90deg, rgba(75,184,248,0.12), rgba(127,216,183,0.08))",
      border: "1px solid rgba(75,184,248,0.35)",
      borderRadius: "6px", fontSize: "13px", color: "#0f172a",
    }}>
      <div>
        <b>{where}</b> has moved to <b>The Floor</b> — the unified operational cockpit.
        This screen is kept for reference / manual overrides only.
      </div>
      <a href="/floor" data-testid="open-in-floor"
         style={{
           background: "#4bb8f8", color: "#04121e", padding: "6px 14px",
           borderRadius: "999px", fontWeight: 700, fontSize: "12px",
           letterSpacing: "0.1em", textDecoration: "none",
         }}>OPEN THE FLOOR →</a>
    </div>
  );
}

export default function TPTripsPanel({ initialTripId = null } = {}) {
  const [trips, setTrips] = useState([]);
  const [providers, setProviders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showPhoneIn, setShowPhoneIn] = useState(false);
  const [assignTrip, setAssignTrip] = useState(null);
  const [offerTrip, setOfferTrip] = useState(null);
  const [broadcastTrip, setBroadcastTrip] = useState(null);
  const [threadTrip, setThreadTrip] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState(initialTripId || null);
  const tripRowRefs = React.useRef({});

  // If AdminDashboard hands us a fresh trip focus (e.g. from global search),
  // pick it up on prop change and scroll the row into view once trips load.
  React.useEffect(() => {
    if (initialTripId) setSelectedTripId(initialTripId);
  }, [initialTripId]);
  React.useEffect(() => {
    if (!selectedTripId) return;
    const el = tripRowRefs.current[selectedTripId];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedTripId, /* re-fire once trips load */ ]);
  const [legTrip, setLegTrip] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshDrivers = useCallback(async () => {
    try {
      const dR = await apiClient.get(`/drivers`);
      setDrivers(dR.data || []);
    } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [tR, pR, dR] = await Promise.all([
        apiClient.get(`${TP}/trips`),
        apiClient.get(`${TP}/providers`),
        apiClient.get(`/drivers`),
      ]);
      setTrips(tR.data || []);
      setProviders(pR.data || []);
      setDrivers(dR.data || []);
    } catch (e) {
      toast.error("Failed to load trips");
    } finally { setLoading(false); }
  }, []);

  // Derived selected trip — always the FRESH row from `trips`, no polling-driven identity churn
  const selectedTrip = useMemo(
    () => (selectedTripId ? trips.find((t) => t.trip_id === selectedTripId) || null : null),
    [selectedTripId, trips]
  );

  // Live-board only shows in-flight trips. Completed / Cancelled / No-show
  // terminal states live in the History → Completed / Cancelled panels.
  // Terminal trips updated <60s ago stay visible so the dispatcher sees the
  // transition; then they drop off automatically.
  const liveTrips = useMemo(() => {
    const TERMINAL = new Set(["COMPLETED", "CANCELLED", "NO_SHOW_CONFIRMED"]);
    const cutoff = Date.now() - 60_000;
    return trips.filter((t) => {
      if (!TERMINAL.has(t.status)) return true;
      // Give the dispatcher a 60-second glance at the terminal state
      const ts = t.updated_at || t.completed_at;
      if (!ts) return false;
      try { return new Date(ts).getTime() >= cutoff; } catch { return false; }
    });
  }, [trips]);

  useEffect(() => { load(); }, [load]);
  // Live updates via SSE — replaces the 6s poll for TP Trips changes.
  // We still poll every 30s as a safety net in case SSE drops.
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${TP}/stream/trips?access_token=${encodeURIComponent(localStorage.getItem("astat_admin_token") || "")}`;
    // EventSource can't send Authorization headers, but our auth middleware
    // also accepts ?access_token=. If SSE fails (older prod build), we fall back to polling.
    let es;
    try {
      es = new EventSource(url);
      es.addEventListener("trip_update", () => {
        // Any change → re-fetch the full list. Cheap because indexed.
        load();
      });
      es.onerror = () => { /* silent — polling picks up the slack */ };
    } catch { /* ignore */ }
    const t = setInterval(load, 30000);
    return () => { if (es) es.close(); clearInterval(t); };
  }, [load]);

  // Load map candidates whenever the SELECTED TRIP ID changes (stable string, no object-identity churn).
  // Also refresh driver positions every 8s WITHOUT re-fitting the map (TPGoogleMap keys fitBounds off pickup/dropoff only).
  useEffect(() => {
    let cancel = false;
    const fetchCand = async () => {
      try {
        const params = selectedTripId ? { trip_id: selectedTripId } : {};
        const r = await apiClient.get(`${TP}/map/candidates`, { params });
        if (!cancel) setCandidates(r.data);
      } catch { if (!cancel) setCandidates(null); }
    };
    fetchCand();
    const iv = setInterval(fetchCand, 8000);
    return () => { cancel = true; clearInterval(iv); };
  }, [selectedTripId]);

  const copyUrl = async (trip) => {
    try {
      const r = await apiClient.get(`${TP}/trips/${trip.trip_id}/driver-url`);
      await navigator.clipboard.writeText(r.data.driver_url);
      toast.success("Driver URL copied");
    } catch (e) { toast.error("Copy failed"); }
  };
  const resend = async (trip) => {
    try {
      const { data } = await apiClient.post(`${TP}/trips/${trip.trip_id}/resend-sms`, {});
      toast.success(data?.push_queued ? "Resent · SMS + push" : "Resent · SMS only (driver has no push registered yet)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
  };

  const showSuggestions = async (trip) => {
    try {
      const { data } = await apiClient.get(`/admin/trips/${trip.trip_id}/suggest-drivers`);
      const cands = data?.data?.candidates || [];
      if (cands.length === 0) {
        toast.warning("No matching drivers online right now. Try Broadcast to widen the pool.");
        return;
      }
      const ready = cands.filter(c => c.production_ready);
      const summary = ready.slice(0, 3).map(c => `${c.name} (${c.distance_mi}mi)`).join(" · ");
      const msg = ready.length > 0
        ? `Top: ${summary}`
        : `${cands.length} candidates but none production-ready. Check credentialing.`;
      toast.success(msg, { duration: 6000, action: { label: "Assign →", onClick: () => setAssignTrip(trip) } });
    } catch (e) {
      const err = e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || e?.message || "Suggest failed";
      toast.error(String(err));
    }
  };
  const assignFromCandidate = async (trip, driver) => {
    if (trip.status === "ASSIGNED" && trip.assigned_driver?.driver_id !== driver.driver_id) {
      const ok = await confirm({
        title: "Reassign this trip?",
        body: `The current driver's link will be revoked and a new SMS will be sent to ${driver.name}. Continue?`,
        confirmLabel: "Reassign",
        tone: "danger",
      });
      if (!ok) return;
    }
    try {
      await apiClient.post(`${TP}/trips/${trip.trip_id}/assign`, { tp_id: driver.tp_id || trip.assigned_tp?.tp_id, driver_id: driver.driver_id });
      toast.success(`Assigned to ${driver.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };

  return (
    <div>
      <FloorMigrationBanner where="TP Trips" />
      <PendingReviewBanner />
      <BroadcastInFlightBanner onSelectTrip={(tripId) => setSelectedTripId(tripId)}/>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate_navy">TP Trips · Live Board</h2>
          <p className="text-sm text-slate-500">Create, assign, broadcast, and track trips across all providers + independent drivers.</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusLegend />
          <button onClick={() => setShowPhoneIn(true)} className="bg-white border-2 border-forest text-forest px-4 py-2 rounded-md text-sm font-semibold" data-testid="phonein-trip-btn">☎ Phone-in ride</button>
          <button onClick={() => setShowCreate(true)} className="bg-forest text-white px-4 py-2 rounded-md text-sm font-semibold" data-testid="new-trip-btn">+ New trip</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <ErrorBoundary name="live map">
            <TPGoogleMap
              pickup={candidates?.pickup}
              dropoff={candidates?.dropoff}
              drivers={candidates?.drivers || []}
              height={460}
              emptyLabel={selectedTrip ? "Loading trip on map…" : "Click a trip below to focus the map on it"}
              onDriverClick={selectedTrip ? (driver) => assignFromCandidate(selectedTrip, driver) : undefined}
            />
          </ErrorBoundary>
        </div>
        <ErrorBoundary name="candidates">
          <ClosestDriverPanel trip={selectedTrip} onAssign={assignFromCandidate}/>
        </ErrorBoundary>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : liveTrips.length === 0 ? (
        <div className="border border-dashed border-[#E2E0D8] rounded-xl p-10 text-center text-slate-500" data-testid="live-trips-empty">
          {trips.length === 0
            ? "No trips yet — create one to get started."
            : "No active trips on the board. Completed & cancelled trips live under History."}
        </div>
      ) : (
        <div className="bg-white border border-[#E9EAE4] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Rider</th>
                <th className="py-2 px-3">Route</th>
                <th className="py-2 px-3">Pickup</th>
                <th className="py-2 px-3">TP</th>
                <th className="py-2 px-3">Driver</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {liveTrips.map((t) => (
                <tr key={t.trip_id}
                  ref={(el) => { if (el) tripRowRefs.current[t.trip_id] = el; }}
                  className={`border-b border-[#E9EAE4] cursor-pointer transition-colors ${selectedTripId === t.trip_id ? "bg-forest/10 ring-2 ring-forest/30" : "hover:bg-slate-50"}`}
                  onClick={() => setSelectedTripId(t.trip_id)}
                  data-testid={`trip-row-${t.trip_id}`}>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_COLORS[t.status] || "bg-slate-100"}`}>{t.status}</span>
                    {t.source === "consumer" && (
                      <span title="Consumer rideshare (MedexOVA Rider)" className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800" data-testid={`rider-badge-${t.trip_id}`}>RIDER</span>
                    )}
                    <AwaitingAcceptBadge trip={t}/>
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-medium text-slate_navy">{t.rider?.name}</div>
                    <div className="text-xs text-slate-500">{t.rider?.mobility_type}</div>
                  </td>
                  <td className="py-2 px-3 text-sm">
                    <div>{(t.pickup?.address || "").split(",")[0]}</div>
                    <div className="text-slate-500 text-xs">→ {(t.dropoff?.address || "").split(",")[0]}</div>
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-500">
                    {t.schedule?.requested_pickup ? new Date(t.schedule.requested_pickup).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="py-2 px-3 text-sm">
                    {t.assigned_tp?.tp_name || (t.source === "consumer" ? <span className="text-blue-700 text-xs font-semibold">MedexOVA Rider</span> : <span className="text-slate-400">unassigned</span>)}
                  </td>
                  <td className="py-2 px-3 text-sm">
                    {t.assigned_driver?.driver_name || "—"}
                    {t.assigned_driver?.driver_phone_masked && <div className="text-xs text-slate-500">{t.assigned_driver.driver_phone_masked}</div>}
                  </td>
                  <td className="py-2 px-3 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                    {["ACCEPTED", "OFFERED_TP", "OFFERED", "REASSIGN_NEEDED", "ASSIGNED", "TP_ACCEPTED"].includes(t.status) && (
                      <>
                        <button onClick={() => setAssignTrip(t)} className="text-xs bg-forest text-white px-2 py-1 rounded-md" data-testid={`assign-btn-${t.trip_id}`}>
                          {["ASSIGNED", "TP_ACCEPTED"].includes(t.status) ? "Reassign" : "Assign"}
                        </button>
                        <button onClick={() => setOfferTrip(t)} className="text-xs bg-brandblue-ink text-white px-2 py-1 rounded-md" data-testid={`offer-btn-${t.trip_id}`} title="Fan this trip out to multiple TPs · first accept wins">Offer</button>
                        <button onClick={() => setBroadcastTrip(t)} className="text-xs border border-[#E2E0D8] px-2 py-1 rounded-md" data-testid={`broadcast-btn-${t.trip_id}`}>Broadcast</button>
                        <button onClick={() => showSuggestions(t)} className="text-xs border border-[#3A7BD5] text-[#3A7BD5] px-2 py-1 rounded-md" data-testid={`suggest-btn-${t.trip_id}`} title="AI suggestion — rank drivers by distance + credentialing + shift">Suggest</button>
                      </>
                    )}
                    {t.driver_url?.token_jti && ["ASSIGNED", "EN_ROUTE", "ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"].includes(t.status) && t.assigned_driver && (
                      <>
                        <button onClick={() => copyUrl(t)} className="text-xs border border-[#E2E0D8] px-2 py-1 rounded-md">Copy link</button>
                        <button onClick={() => resend(t)} className="text-xs border border-[#E2E0D8] px-2 py-1 rounded-md">Resend</button>
                      </>
                    )}
                    <button
                      onClick={() => setLegTrip(t)}
                      className="text-xs border border-[#E2E0D8] px-2 py-1 rounded-md"
                      data-testid={`add-leg-btn-${t.trip_id}`}
                      title="Chain a return / additional leg to this trip"
                    >
                      + Return leg
                    </button>
                    <button
                      onClick={() => setThreadTrip(t)}
                      className="text-xs border border-[#E2E0D8] px-2 py-1 rounded-md"
                      data-testid={`message-btn-${t.trip_id}`}
                      title="Unified SMS thread + share tracking link"
                    >
                      Message
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateTripDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => load()}/>
      <PhoneInTripModal open={showPhoneIn} onClose={() => setShowPhoneIn(false)} onCreated={() => load()}/>
      <AssignDialog trip={assignTrip} providers={providers} drivers={drivers} onClose={() => setAssignTrip(null)} onAssigned={() => load()} refreshDrivers={refreshDrivers}/>
      <OfferToTPsDialog trip={offerTrip} providers={providers} drivers={drivers} onClose={() => setOfferTrip(null)} onOffered={() => load()}/>
      <BroadcastDialog trip={broadcastTrip} providers={providers} onClose={() => setBroadcastTrip(null)} onDone={load}/>
      <AddLegDialog trip={legTrip} onClose={() => setLegTrip(null)} onCreated={() => { setLegTrip(null); load(); }}/>
      <TripThreadDialog trip={threadTrip} onClose={() => setThreadTrip(null)}/>
      <OnboardingTour />
    </div>
  );
}
